import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatSize, formatLength } from '../../constants/serviceOptions';
import type { TaskPricing } from '../../types/appointments';

export interface TaskPricingTableProps {
	/** Rows for a single task type of a single service (one serviceTaskId). */
	pricings: TaskPricing[];
	onEdit: (pricing: TaskPricing) => void;
	onDelete: (pricing: TaskPricing) => void;
}

/**
 * Renders a task type's pricing rows with per-row edit/delete. Used by both the
 * By Service and By Task views. A row's identity is its serviceTaskId + size +
 * length; serviceTaskId is unique per (service, task type), so size+length is
 * unique within the rows shown here. Add Size lives beside the group header in
 * the parent, next to the task/service name.
 */
const TaskPricingTable = ({ pricings, onEdit, onDelete }: TaskPricingTableProps) => (
	<table className="tasks-tab__pricing-table">
		<thead>
			<tr>
				<th>Size</th>
				<th>Length</th>
				<th>Duration</th>
				<th>Price</th>
				<th className="text-right">Actions</th>
			</tr>
		</thead>
		<tbody>
			{pricings.map(pricing => (
				<tr key={`${pricing.serviceTaskId}|${pricing.size ?? ''}|${pricing.length ?? ''}`}>
					<td>{formatSize(pricing.size)}</td>
					<td>{formatLength(pricing.length)}</td>
					<td>{pricing.durationMinutes} min</td>
					<td>${Number(pricing.price).toFixed(2)}</td>
					<td className="text-right">
						<div className="flex items-center justify-end gap-1">
							<button
								type="button"
								title="Edit pricing"
								onClick={() => onEdit(pricing)}
								className="p-1 rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400 transition-colors cursor-pointer"
							>
								<PencilIcon className="w-3.5 h-3.5" />
							</button>
							<button
								type="button"
								title="Delete size"
								onClick={() => onDelete(pricing)}
								className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors cursor-pointer"
							>
								<TrashIcon className="w-3.5 h-3.5" />
							</button>
						</div>
					</td>
				</tr>
			))}
		</tbody>
	</table>
);

export default TaskPricingTable;
